import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';

export const addNewBlankPage = async (): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  const currentPageHashedId = poster.getCurrentPageId();
  const pageOrder = poster.pages.pageOrder;
  const currentPageIndex = pageOrder.indexOf(currentPageHashedId);
  // When no current page is selected (indexOf === -1), append to the end instead of inserting at position 0,
  // which would cause addNewPage to read pagesInOrder[-1].background and crash.
  const targetOrder = currentPageIndex === -1 ? pageOrder.length : currentPageIndex + 1;
  await addNewPage(targetOrder);
};

export const addNewPage = async (order?: number): Promise<void> => {
  if (!window.posterEditor?.whiteboard?.pages) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  const pagesInOrder = window.posterEditor.whiteboard.pages.getPagesInOrder();
  const pageOrder = order ?? pagesInOrder?.length;

  if (pageOrder === window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  const sourcePage = pagesInOrder[pageOrder - 1];
  const sourceBgs = sourcePage?.items.getBackgrounds() ?? [];
  const itemsHashMap: Record<string, ItemObject> = {};
  const itemsOrder: string[] = [];
  // Multiple backgrounds means a timeline-sequenced background (e.g. video/slideshow); only carry
  // over a single static background onto the new page.
  if (sourceBgs.length === 1) {
    const cloned = sourceBgs[0].toObjectWithNewUids() as ItemObject;
    itemsHashMap[cloned.uid] = cloned;
    itemsOrder.push(cloned.uid);
  }
  const page = await window.posterEditor.whiteboard.pages.addPage({items: {itemsHashMap, itemsOrder}}, {pageOrder});
  scrollToPage(page.hashedID);
  page.poster.setCurrentPage(page.hashedID);
};

export const scrollToPage = (hashedId: string, animate = true): void => {
  const ANIMATION_DURATION = 300;
  const page = window.posterEditor?.whiteboard?.pages.getPage(hashedId);
  const pageOffsetTop = page?.pageContainer.offsetTop;
  const whiteboardContainer = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER];
  if (whiteboardContainer && typeof pageOffsetTop === 'number') {
    page?.refreshFullCanvas();
    if (animate) {
      whiteboardContainer.animate({scrollTop: pageOffsetTop}, ANIMATION_DURATION, () => {
        window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
      });
    } else {
      whiteboardContainer.scrollTop(pageOffsetTop);
      window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
    }
  }
};

export const addDuplicatedPage = async (pageToDuplicate: Page): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  const pages = poster?.pages;
  if (!pages || !poster) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  if (pages.pageOrder.length >= window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  // A duplicated page reuses the same Getty assets (identical hashedFilenames), so it can never raise the unique Getty count — only unique assets count toward the limit, so no limit check is needed here.
  const duplicatedPageObject = pageToDuplicate.getDuplicatedPageObject();

  const pageOrder = (pages.getPageOrder(pageToDuplicate.hashedID) ?? 0) + 1;
  const page = await pages.addPage(duplicatedPageObject, {pageOrder, updateRedux: true, immediatelySelectOnceAdded: true});

  scrollToPage(page.hashedID);
};
